import React from "react";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { WHATSAPP, whatsappWith, openExternal } from "@/lib/contact";
import HeroBackground from "@/components/HeroBackground";
import HeroStatStrip from "@/components/hero/HeroStatStrip";
import LivePrintCard from "@/components/hero/LivePrintCard";
import MaterialsMarquee from "@/components/hero/MaterialsMarquee";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center overflow-hidden bp-grid"
    >
      <HeroBackground />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-2 h-2 bg-[#FF5722] pulse-dot rounded-full" />
              <span className="label-overline">
                Precision Additive Studio · Bengaluru
              </span>
            </div>

            <h1 className="font-display text-5xl sm:text-7xl lg:text-[88px] leading-[0.95] font-semibold tracking-tighter">
              <span className="word-in" style={{ animationDelay: "0ms" }}>Shaping</span><br />
              <span className="word-in text-zinc-400" style={{ animationDelay: "180ms" }}>imagination</span><br />
              <span className="word-in" style={{ animationDelay: "360ms" }}>into </span>
              <span className="word-in text-[#FF5722]" style={{ animationDelay: "540ms" }}>reality.</span>
            </h1>

            <p className="mt-8 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed word-in" style={{ animationDelay: "720ms" }}>
              High-quality, affordable 3D printing & CAD modeling out of Gunjur
              Village. From engineering prototypes and college STEM kits to
              architectural lighting and bespoke decor — all under one roof.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 word-in" style={{ animationDelay: "880ms" }}>
              <a
                data-testid="hero-quote-button"
                href="#quote"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("quote");
                }}
                className="group inline-flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#F4511E] text-white px-7 h-14 text-sm font-medium tracking-wide transition-all glow-pulse"
              >
                <ArrowUpRight size={16} className="rotate-180" />
                Drop your STL — get instant quote
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <a
                data-testid="hero-ai-button"
                href={whatsappWith("Hi 3D PRINT HUB, I have a quick question about a print.")}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => openExternal(e, whatsappWith("Hi 3D PRINT HUB, I have a quick question about a print."))}
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#25D366] hover:text-[#25D366] px-7 h-14 text-sm font-medium tracking-wide transition-colors"
              >
                <MessageCircle size={16} />
                Chat on WhatsApp
              </a>
            </div>

            <HeroStatStrip />
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <LivePrintCard />
          </div>
        </div>
      </div>

      <MaterialsMarquee />
    </section>
  );
}
